// Agent identity derivation, ported from origin/dashboard/src/core/transforms.js.
// Maps a provider tag (canonical `anthropic`/`openai`/…), a legacy agent_type
// enum (numeric or "AI_AGENT_TYPE_*"), or hint-bearing free text to a
// normalized { key, name } pair. The key is the lookup token used by
// LOGO_DEFS and AGENT_COLORS. The legacy enum paths remain because the
// endpoint-agent-inventory REST shape still returns raw agent_type strings —
// analytics-api itself has migrated to PROMPT_PROVIDER.

export const KNOWN_AGENT_HINTS = [
  { key: "claude", name: "Claude", tokens: ["CLAUDE", "ANTHROPIC"] },
  { key: "gemini", name: "Gemini", tokens: ["GEMINI", "GOOGLE_AI", "BARD"] },
  // "Vertex AI", "Google Vertex", "Google Vertex AI" all name the one product.
  { key: "vertex", name: "Vertex AI", tokens: ["VERTEX"] },
  { key: "codex", name: "Codex", tokens: ["CODEX", "CODEGEN"] },
  { key: "cursor", name: "Cursor", tokens: ["CURSOR"] },
  // xAI's CLI reports itself as "grok-build"/"Grok CLI"; without a hint that
  // free text slugifies to its own key and misses the brand's color, while
  // the provider tag `xai` already resolves here. "GROK" does not collide
  // with Groq (`GROQ`), which is a different vendor.
  { key: "xai", name: "xAI", tokens: ["GROK", "XAI"] },
  { key: "perplexity", name: "Perplexity", tokens: ["PERPLEXITY", "COMET"] },
  // chatgpt-desktop must come before chatgpt: 'CHATGPT_DESKTOP' contains 'CHATGPT'.
  // The space-separated token matches the agent-reported product name
  // ("ChatGPT Desktop"), which the underscore variants never hit.
  {
    key: "chatgpt-desktop",
    name: "ChatGPT Desktop",
    tokens: ["CHATGPT_DESKTOP", "CHATGPTDESKTOP", "CHATGPT DESKTOP"],
  },
  // m365copilot must come before copilot: 'M365COPILOT' contains 'COPILOT'.
  // Space-separated tokens match the product names "Microsoft 365 Copilot"
  // / "M365 Copilot", which otherwise fell through to plain COPILOT.
  {
    key: "m365copilot",
    name: "M365 Copilot",
    tokens: [
      "M365_COPILOT",
      "M365COPILOT",
      "M365 COPILOT",
      "MICROSOFT 365 COPILOT",
      // Copilot surfaced inside Word/Excel/PowerPoint rather than as its own
      // app. Shares this mark because it is the same product; without an entry
      // above plain COPILOT it would take Microsoft Copilot's, a different one.
      "COPILOT IN OFFICE",
    ],
  },
  // github-copilot must come before copilot: 'GITHUB COPILOT' contains 'COPILOT'.
  // GitHub Copilot (the dev tool - CLI and desktop app) and Microsoft Copilot
  // (the consumer assistant) are different products that shared one key, so the
  // inventory showed one mark for both. `copilot` keeps the Microsoft side,
  // which is also where the coarse `microsoft` provider tag lands.
  {
    key: "github-copilot",
    name: "GitHub Copilot",
    tokens: ["GITHUB_COPILOT", "GITHUBCOPILOT", "GITHUB COPILOT"],
  },
  { key: "copilot", name: "Copilot", tokens: ["COPILOT"] },
  {
    key: "codewhisperer",
    name: "CodeWhisperer",
    tokens: ["CODEWHISPERER", "AMAZON_Q", "AMAZON Q"],
  },
  { key: "tabnine", name: "Tabnine", tokens: ["TABNINE"] },
  { key: "deepseek", name: "DeepSeek", tokens: ["DEEPSEEK"] },
  { key: "chatgpt", name: "ChatGPT", tokens: ["CHATGPT", "OPENAI"] },
  { key: "windsurf", name: "Windsurf", tokens: ["WINDSURF", "CODEIUM"] },
  { key: "openclaw", name: "OpenClaw", tokens: ["OPENCLAW", "OPEN_CLAW"] },
  { key: "qwen", name: "Qwen", tokens: ["QWEN", "ALIBABA_CLOUD"] },
  { key: "together-ai", name: "Together AI", tokens: ["TOGETHERAI", "TOGETHER AI", "TOGETHER_AI"] },
  // Multi-product brands whose members must share one brand key the way
  // Claude Code / Claude Desktop do - without a hint each product slugifies
  // to its own key ("antigravity-cli", "antigravity-ide", ...) and misses the
  // brand's LOGO_DEFS / AGENT_COLORS entry. Last in the cascade because
  // "FACTORY" is a common word in unrelated paths and class names: a
  // genuinely branded token elsewhere in the list should win first.
  { key: "antigravity", name: "Antigravity", tokens: ["ANTIGRAVITY"] },
  {
    key: "factory",
    name: "Factory",
    tokens: [
      "FACTORY_DROID",
      "FACTORY DROID",
      "FACTORY_DESKTOP",
      "FACTORY DESKTOP",
      "FACTORY.AI",
      "FACTORY",
    ],
  },
];

export const BROWSER_PROCESS_NAMES = new Set([
  "chrome",
  "chrome.exe",
  "google chrome",
  "chromium",
  "chromium.exe",
  "msedge",
  "msedge.exe",
  "microsoft edge",
  "edge",
  "firefox",
  "firefox.exe",
  "mozilla firefox",
  "safari",
  "safari.exe",
  "opera",
  "opera.exe",
  "brave",
  "brave.exe",
  "vivaldi",
  "vivaldi.exe",
  "arc",
  "arc.exe",
]);

const GENERIC_PROCESS_NAMES = new Set([
  "node",
  "nodejs",
  "npm",
  "pnpm",
  "yarn",
  "bun",
  "bash",
  "sh",
  "zsh",
  "fish",
  "dash",
  "python",
  "python3",
  "py",
  "pip",
  "powershell",
  "pwsh",
  "cmd",
  "cmd.exe",
  "java",
  "ruby",
  "perl",
  "php",
  "dotnet",
  "go",
  "deno",
  "sudo",
  "main",
  "index",
  "server",
  "app",
  "start",
  "cli",
  "unknown",
]);

export function slugifyAgentName(name = "") {
  return (
    name
      .trim()
      .toLowerCase()
      // Strip only real executable/package extensions — a generic
      // strip-any-extension turned the product name "llama.cpp" into "llama"
      // (colliding with anything else llama-branded).
      .replace(/\.(exe|app|bat|cmd|com|sh|dmg|msi|appimage)$/i, "")
      .replace(/[^a-z0-9@]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-{2,}/g, "-")
  );
}

export function humanizeAgentName(agentKey = "") {
  if (!agentKey) return "Unknown Agent";
  const cleaned = agentKey.replace(/^@/, "").replace(/[_-]+/g, " ").trim();
  if (!cleaned) return "Unknown Agent";
  return cleaned
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

function extractProcessName(str) {
  if (!str) return null;
  const segments = String(str)
    .split(/[/\\]+/)
    .filter(Boolean);
  if (!segments.length) return null;
  return segments[segments.length - 1]
    .replace(/\.[a-z0-9]+$/i, "")
    .toLowerCase()
    .trim();
}

export function isBrowserProcess(imageName = "", commandLine = "") {
  const imageProc = extractProcessName(imageName);
  const cmdProc = extractProcessName((commandLine || "").split(/\s+/)[0]);
  if (
    imageProc &&
    (BROWSER_PROCESS_NAMES.has(imageProc) ||
      BROWSER_PROCESS_NAMES.has(imageProc + ".exe"))
  )
    return true;
  if (
    cmdProc &&
    (BROWSER_PROCESS_NAMES.has(cmdProc) ||
      BROWSER_PROCESS_NAMES.has(cmdProc + ".exe"))
  )
    return true;
  const combined = `${imageName} ${commandLine}`.toLowerCase();
  if (combined.includes("google chrome") || combined.includes("chromium"))
    return true;
  if (combined.includes("microsoft edge") || combined.includes("msedge"))
    return true;
  if (combined.includes("mozilla firefox")) return true;
  if (combined.includes("safari") && combined.includes("apple")) return true;
  return false;
}

function extractExecutablePath(commandLine = "") {
  const trimmed = (commandLine || "").trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('"')) {
    const end = trimmed.indexOf('"', 1);
    return end > 1 ? trimmed.substring(1, end) : trimmed.substring(1);
  }
  if (trimmed.startsWith("'")) {
    const end = trimmed.indexOf("'", 1);
    return end > 1 ? trimmed.substring(1, end) : trimmed.substring(1);
  }
  const sp = trimmed.search(/\s/);
  return sp > 0 ? trimmed.substring(0, sp) : trimmed;
}

export function extractProcessHint(commandLine = "", imageName = "") {
  const candidates = [];
  const push = (value, weight) => {
    if (!value) return;
    const cleaned = value.replace(/^["']|["']$/g, "").trim();
    if (cleaned) candidates.push({ value: cleaned, weight });
  };
  const processPath = (path) => {
    if (!path) return;
    const pkgMatch = path.match(/node_modules[/\\](@?[\w.-]+)/i);
    if (pkgMatch && pkgMatch[1]) push(pkgMatch[1], 4);
    const segs = path.split(/[/\\]+/).filter(Boolean);
    if (segs.length) {
      const last = segs[segs.length - 1];
      push(last, 3);
      if (segs.length > 1 && /^(index|main)\.[\w]+$/i.test(last))
        push(segs[segs.length - 2], 2);
    }
  };
  processPath(imageName);
  processPath(extractExecutablePath(commandLine));
  const scored = candidates
    .map(({ value, weight }) => ({
      value,
      weight,
      base: value.replace(/\.[a-z0-9]+$/i, "").toLowerCase(),
    }))
    .filter(({ base }) => base && !GENERIC_PROCESS_NAMES.has(base));
  scored.sort((a, b) => b.weight - a.weight);
  return scored.length ? scored[0].value : null;
}

const ENUM_INT_TO_IDENTITY = {
  1: { key: "claude", name: "Claude" },
  2: { key: "codex", name: "Codex" },
  3: { key: "gemini", name: "Gemini" },
  4: { key: "cursor", name: "Cursor" },
  5: { key: "copilot", name: "Copilot" },
  6: { key: "deepseek", name: "DeepSeek" },
  7: { key: "chatgpt", name: "ChatGPT" },
  8: { key: "m365copilot", name: "M365 Copilot" },
  9: { key: "chatgpt-desktop", name: "ChatGPT Desktop" },
  10: { key: "openclaw", name: "OpenClaw" },
};

// Canonical builtin provider tags from shared-protos BUILTIN_PROVIDERS.
// `openai` and `microsoft` are coarse: they cover ChatGPT/Codex/ChatGPT-Desktop
// and Copilot/M365Copilot respectively, so a row that ALSO carries the legacy
// agent_type enum should disambiguate via that — handled by ordering in
// deriveAgentIdentity (enum check runs first).
export const PROVIDER_TAG_TO_IDENTITY = {
  anthropic: { key: "claude", name: "Claude" },
  google: { key: "gemini", name: "Gemini" },
  cursor: { key: "cursor", name: "Cursor" },
  deepseek: { key: "deepseek", name: "DeepSeek" },
  openai: { key: "chatgpt", name: "ChatGPT" },
  microsoft: { key: "copilot", name: "Copilot" },
  groq: { key: "groq", name: "Groq" },
  mistral: { key: "mistral", name: "Mistral" },
  openrouter: { key: "openrouter", name: "OpenRouter" },
  perplexity: { key: "perplexity", name: "Perplexity" },
  "together-ai": { key: "together-ai", name: "Together AI" },
  huggingface: { key: "huggingface", name: "HuggingFace" },
  xai: { key: "xai", name: "xAI" },
  // On-device runtimes (Ollama / LM Studio / llama.cpp / transformers),
  // captured via the local-AI-capture endpoint toggle (O-1110).
  local: { key: "local", name: "Local" },
  // Relay/router — the inventory tags the relay itself (its traffic reports
  // the provider it forwards to). Non-builtin custom tag, like factory.
  openclaw: { key: "openclaw", name: "OpenClaw" },
  "vertex-ai": { key: "vertex", name: "Vertex AI" },
  // The LiteLLM gateway's custom tag: its prompts span many model makers.
  litellm: { key: "litellm", name: "LiteLLM" },
  "microsoft-foundry": { key: "microsoft-foundry", name: "Microsoft Foundry" },
};

// Product surfaces a capture producer stamps as `client_surface`
// (kebab-case). They name the product when the provider tag cannot: GitHub
// Copilot captures carry the coarse `microsoft` provider, which on its own
// resolves to Microsoft Copilot, a different product.
export const CLIENT_SURFACE_IDENTITIES = {
  "copilot-vscode": { key: "github-copilot", name: "GitHub Copilot", surface: "VS Code" },
  "copilot-cli": { key: "github-copilot", name: "GitHub Copilot", surface: "CLI" },
  // Reported by the Workspace browser parser from the app Gemini answered in.
  "gemini-docs": { key: "gemini", name: "Gemini", surface: "Docs" },
  "gemini-sheets": { key: "gemini", name: "Gemini", surface: "Sheets" },
  "gemini-slides": { key: "gemini", name: "Gemini", surface: "Slides" },
};

// { key, name, surface } for a known client surface, else null.
export function clientSurfaceIdentity(clientSurface) {
  const tag = typeof clientSurface === "string" ? clientSurface.trim().toLowerCase() : "";
  return (tag && CLIENT_SURFACE_IDENTITIES[tag]) || null;
}

export function deriveAgentIdentity(
  agentType,
  commandLine = "",
  imageName = "",
  provider = "",
  clientSurface = "",
) {
  // A producer-stamped surface is the most specific statement available, so
  // it outranks both the legacy enum and the coarse provider tag.
  const surfaceHit = clientSurfaceIdentity(clientSurface);
  if (surfaceHit) return { key: surfaceHit.key, name: surfaceHit.name };
  // Enum check runs before the provider tag check so callers that have
  // both an agent_type and a coarse provider tag (e.g. agent_type=Codex
  // with provider="openai") resolve to the specific identity.
  if (typeof agentType === "number" && ENUM_INT_TO_IDENTITY[agentType]) {
    return ENUM_INT_TO_IDENTITY[agentType];
  }
  if (typeof agentType === "string" && ENUM_INT_TO_IDENTITY[agentType]) {
    return ENUM_INT_TO_IDENTITY[agentType];
  }
  // Canonical provider tags are lower-kebab, but legacy agent_type values
  // arrive PascalCase (e.g. "Openrouter") — normalize so both resolve.
  if (provider && typeof provider === "string") {
    const hit = PROVIDER_TAG_TO_IDENTITY[provider.toLowerCase()];
    if (hit) return hit;
  }
  if (typeof agentType === "string") {
    const hit = PROVIDER_TAG_TO_IDENTITY[agentType.toLowerCase()];
    if (hit) return hit;
  }

  // Hint-token cascade. Run before the browser-process guard so apps
  // like M365 Copilot (which embed Edge) don't get classified as
  // "browser" when their agent_type string is authoritative.
  const haystack = [agentType, commandLine, imageName]
    .filter(Boolean)
    .map((v) => String(v).toUpperCase())
    .join(" ");
  for (const hint of KNOWN_AGENT_HINTS) {
    if (hint.tokens.some((t) => haystack.includes(t))) {
      return { key: hint.key, name: hint.name };
    }
  }

  if (isBrowserProcess(imageName, commandLine)) {
    return { key: "browser", name: "Browser" };
  }

  if (typeof agentType === "string") {
    const enumMatch = agentType.toUpperCase().match(/AI_AGENT_TYPE_([\w.-]+)/);
    if (enumMatch && enumMatch[1]) {
      const key = slugifyAgentName(enumMatch[1]);
      return { key: key || "unknown", name: humanizeAgentName(key) };
    }
  }

  const procHint = extractProcessHint(commandLine, imageName);
  if (procHint) {
    const key = slugifyAgentName(procHint);
    return { key: key || "unknown", name: humanizeAgentName(key) };
  }

  if (typeof agentType === "string") {
    const key = slugifyAgentName(agentType);
    if (key) return { key, name: humanizeAgentName(key) };
  }

  return { key: "unknown", name: "Unknown Agent" };
}

// The finite value space of the deprecated events.v1.AIAgentType enum as it
// appears in stored data (PascalCase strum names), mapped to human labels.
// This is deliberately a closed map, not a substring cascade: anything NOT
// in it is an agent-reported product name ("Claude Code", "Ollama",
// "llama.cpp") and must be shown verbatim.
const LEGACY_ENUM_LABELS = {
  Claude: "Claude",
  Codex: "Codex",
  Gemini: "Gemini",
  Cursor: "Cursor",
  Copilot: "Copilot",
  Deepseek: "DeepSeek",
  Chatgpt: "ChatGPT",
  // Canonical product name, matching shared-protos/fixtures/ai_tool_display_names.json.
  // The agent reports "Microsoft 365 Copilot" once it emits tool names; labelling the
  // legacy enum the same way lets the two fold into one segment instead of appearing
  // as two entries for one product.
  M365Copilot: "Microsoft 365 Copilot",
  ChatgptDesktop: "ChatGPT Desktop",
  Openclaw: "OpenClaw",
  Groq: "Groq",
  Mistral: "Mistral",
  Openrouter: "OpenRouter",
  Perplexity: "Perplexity",
  TogetherAi: "Together AI",
  HuggingFace: "HuggingFace",
  Xai: "xAI",
  Unspecified: "Unspecified",
};

// Product-level display label for an installed-agent value. The inventory
// pipeline reports the actual product name in tool_name ("Claude Code",
// "Claude Desktop", "Ollama") — show it verbatim; folding it through
// deriveAgentIdentity erased exactly the distinction the name carries
// ("Claude Code" and "Claude Desktop" both rendered as "Claude"). Only the
// known legacy enum strings (rows written before the agent-name rekey, or
// endpoints on old agents) get humanized.
export function agentDisplayLabel(raw) {
  const value = typeof raw === "string" ? raw.trim() : "";
  if (!value) return "Unknown Agent";
  return LEGACY_ENUM_LABELS[value] ?? value;
}

// Every legacy agent-type enum string the inventory can still report. Rows written
// before the agent-name rollout carry these until the endpoint's next metadata
// change, so they are a live part of the reportable set even though they are not in
// shared-protos/fixtures/ai_tool_display_names.json. Exported so the brand-mark
// coverage test can walk them instead of only the fixture.
export const LEGACY_AGENT_ENUM_NAMES = Object.freeze(Object.keys(LEGACY_ENUM_LABELS));

// Display label for a prompt's model.
//
// Cursor is captured via hooks only (no proxy response stream), so it never
// yields a model-resolved token observation (TOKEN entity) — the
// analytics-derived model comes back empty —
// and in Auto mode its hook envelope reports the sentinel "default". Surface
// Cursor's own "Auto" label rather than a misleading "unknown"/"default". A
// pinned Cursor model name lives only in the request body, which isn't wired
// into the model field yet (tracked in O-1307).
export function modelLabel(model, provider) {
  const missing = !model || model === "default" || model === "unknown";
  if (missing && String(provider || "").toLowerCase() === "cursor") {
    return "Auto";
  }
  return model || "unknown";
}

const COPILOT_MODEL_PREFIX = "m365-copilot-";

// The `appClass` vocabulary is open (Microsoft documents
// `IPM.SkypeTeams.Message.Copilot.<application>`, "and many more"), so read the
// suffix rather than match a list; callers uppercase it, so no casing table.
export function copilotSurface(model) {
  const tag = typeof model === "string" ? model.trim() : "";
  if (!tag.startsWith(COPILOT_MODEL_PREFIX)) return null;
  return tag.slice(COPILOT_MODEL_PREFIX.length) || null;
}
